import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRoutesList } from 'routes/api/routesApi';
import { Route } from 'shared/types/ObjectTypes';
import RoutesList from './RoutesList';
import { useAllOrganizations, getUserPermissions } from 'workspaces/api';
import { useListQuery } from 'shared/api/useListQuery';
import { queryKeys } from 'shared/api/queryKeys';
import { page_size } from 'shared/api/apiUtils';
import { useKeycloakToken } from 'shared/auth/useKeycloak';
import {
  useDestinationTypes,
  useDestinationUrls,
  useProviderTypes,
} from 'shared/api/useFilterOptions';
import { useKeycloak } from '@react-keycloak/web';
import { useHelp } from 'shared/context/HelpContext';
import { useTranslation } from 'react-i18next';
import { hideReverseRoutes } from './hideReverseRoutes';

const RoutesPage = () => {
  const { t, i18n } = useTranslation();
  const { setHelpContent } = useHelp();

  useEffect(() => {
    setHelpContent({
      cards: [
        {
          title: t('Managing Routes in Gundi'),
          description: t(
            'Learn how to create and manage Routes between data sources and destinations.',
          ),
          button: {
            label: t('Learn More'),
            url: 'https://support.earthranger.com/en_US/routes-gundi/routes',
          },
        },
      ],
    });
  }, [i18n.language, t, setHelpContent]);

  useEffect(() => {
    return () => setHelpContent(null);
  }, [setHelpContent]);

  type FilterTypes = {
    search: string;
    provider_type__in: FilterTags[];
    destination_type__in: FilterTags[];
    destination_url__in: FilterTags[];
    owner__in: FilterTags[];
    status: FilterTags[];
    ordering: string;
  };
  type FilterTags = {
    name: string;
    id: string;
  };
  const defaultRoutesFilter = {
    provider_type__in: [],
    destination_type__in: [],
    destination_url__in: [],
    owner__in: [],
    status: [],
    search: '',
    ordering: 'name',
  };
  const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
  const { keycloak } = useKeycloak();
  const [canAddRoute, setCanAddRoute] = useState(false);
  const [routesFilter, setRoutesFilter] = useState<FilterTypes>(() => {
    const savedFilter = sessionStorage.getItem('routesFilter');
    return savedFilter
      ? { ...defaultRoutesFilter, ...JSON.parse(savedFilter) }
      : defaultRoutesFilter;
  });
  const [offset, setOffset] = useState<number>(0);
  const routesQuery = useMemo(
    () => ({
      provider_type__in: routesFilter.provider_type__in
        .map((a) => a.id)
        .toString(),
      destination_type__in: routesFilter.destination_type__in
        .map((a) => a.id)
        .toString(),
      destination_url__in: routesFilter.destination_url__in
        .map((a) => a.id)
        .toString(),
      owner__in: routesFilter.owner__in.map((a) => a.id).toString(),
      status: routesFilter.status.map((a) => a.id).toString(),
      ordering: routesFilter.ordering,
      search: routesFilter.search,
      offset: offset,
      previous:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/routes/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (offset - page_size),
      next:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/routes/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (offset + page_size),
    }),
    [routesFilter, offset],
  );
  const routesList = useListQuery<Route>(
    queryKeys.routes.list(routesQuery),
    (token) => getRoutesList(token, routesQuery),
  );
  // One row per flow: the reverse Route of a bidirectional flow is
  // already an arrow on its partner's map. GUNDI-5680 temporary,
  // client-side and same-page only (count stays the server's) —
  // see the reviewer note in hideReverseRoutes.ts; GUNDI-5681 retires it.
  const routes = useMemo(
    () => hideReverseRoutes(routesList.results),
    [routesList.results],
  );
  // Settles on the first answer even if it failed, so the page never hangs on
  // its skeleton, and stays settled on later filter or page changes.
  const pageLoaded = routesList.settled;
  // Filter options wait for the Routes table so they don't compete with it.
  const { organizations, loadingOrganizations } = useAllOrganizations({
    enabled: pageLoaded,
  });
  const { destinationUrls, loadingDestinationUrls } = useDestinationUrls({
    enabled: pageLoaded,
  });
  const { providerTypes, loadingProviderTypes } = useProviderTypes({
    enabled: pageLoaded,
  });
  const { destinationTypes, loadingDestinationTypes } = useDestinationTypes({
    enabled: pageLoaded,
  });
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem('routesFilter', JSON.stringify(routesFilter));
  }, [routesFilter]);

  useEffect(() => {
    if (!pageLoaded) return;
    const loadPerms = async () => {
      if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
        try {
          const temp = await getUserPermissions(token);
          setCanAddRoute(temp);
        } catch (err) {
          console.log(err);
        }
      }
    };
    loadPerms();
  }, [pageLoaded]);

  const handleGlobalSearch = (searchSubStr: string) => {
    setRoutesFilter((prevFilter: any) => ({
      ...prevFilter,
      search: searchSubStr,
    }));
    setOffset(0);
  };

  const handleFilterByStatus = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setRoutesFilter((prev) => ({ ...prev, status: tags }));
    setOffset(0);
  };

  const handleFilterDestinationUrl = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setRoutesFilter((prev) => ({ ...prev, destination_url__in: tags }));
    setOffset(0);
  };

  const handleFilterOwner = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setRoutesFilter((prev) => ({ ...prev, owner__in: tags }));
    setOffset(0);
  };

  const handleFilterProviderType = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setRoutesFilter((prev) => ({ ...prev, provider_type__in: tags }));
    setOffset(0);
  };

  const handleFilterDestinationType = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setRoutesFilter((prev) => ({ ...prev, destination_type__in: tags }));
    setOffset(0);
  };

  const handleSortBy = (selected: { label: string; value: string }) => {
    setRoutesFilter((prev) => ({ ...prev, ordering: selected.value }));
    setOffset(0);
  };

  const handleSelectRoute = (route_id: string, subpage?: string) => {
    navigate('/routes/' + route_id + (subpage ? '/' + subpage : ''));
  };

  const handlePageClick = (e: any) => {
    setOffset(e.selected * page_size);
  };

  const handleClickAdd = () => {
    navigate('/routes/new');
  };

  const toSingleValue = (tags: FilterTags[]) =>
    tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

  const statusSelectValue = toSingleValue(routesFilter.status);
  const destinationSelectValue = toSingleValue(
    routesFilter.destination_url__in,
  );
  const ownerSelectValue = toSingleValue(routesFilter.owner__in);
  const providerTypeSelectValue = toSingleValue(routesFilter.provider_type__in);
  const destinationTypeSelectValue = toSingleValue(
    routesFilter.destination_type__in,
  );

  return (
    <div>
      <RoutesList
        canAddRoute={canAddRoute}
        routes={routes}
        reloadRoutes={routesList.loading}
        organizations={organizations}
        destinationUrls={destinationUrls}
        providerTypes={providerTypes}
        loadingProviderTypes={loadingProviderTypes}
        destinationTypes={destinationTypes}
        loadingDestinationTypes={loadingDestinationTypes}
        onSelectRoute={handleSelectRoute}
        onChangeGlobalSearch={handleGlobalSearch}
        onRefresh={() => routesList.refresh()}
        onChangeStatusFilter={handleFilterByStatus}
        statusFilterValue={statusSelectValue}
        onChangeDestinationUrl={handleFilterDestinationUrl}
        destinationFilterValue={destinationSelectValue}
        loadingDestinationUrls={loadingDestinationUrls}
        onChangeOwner={handleFilterOwner}
        ownerFilterValue={ownerSelectValue}
        loadingOrganizations={loadingOrganizations}
        onChangeProviderType={handleFilterProviderType}
        providerTypeFilterValue={providerTypeSelectValue}
        onChangeDestinationType={handleFilterDestinationType}
        destinationTypeFilterValue={destinationTypeSelectValue}
        globalSearchStr={routesFilter.search}
        ordering={routesFilter.ordering}
        onChangeSortBy={handleSortBy}
        onPageClicked={handlePageClick}
        totalCount={routesList.count}
        pageLoaded={pageLoaded}
        take={page_size}
        offset={offset}
        onClickAdd={handleClickAdd}
      />
    </div>
  );
};

export default RoutesPage;
